import { useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, X, Camera, FileText, MessageSquare, Receipt, Wrench, Scale, ShieldCheck, BookText, Search } from "lucide-react";

// Feature 34 — visual, owner-scoped source picker. Lists the tenant's real confirmed records
// (photo thumbnails, docs, payments, etc.) so a statement can cite the EXACT source without ever
// typing an internal record ID.
const SECTIONS = [
  { key: "photo", label: "Photos", Icon: Camera },
  { key: "document", label: "Documents", Icon: FileText },
  { key: "communication", label: "Messages", Icon: MessageSquare },
  { key: "payment", label: "Payments", Icon: Receipt },
  { key: "repair_event", label: "Maintenance", Icon: Wrench },
  { key: "charge", label: "Landlord charges", Icon: Scale },
  { key: "sealed_capsule", label: "Sealed capsules", Icon: ShieldCheck },
  { key: "lease_fact", label: "Lease facts", Icon: BookText },
];

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(); } catch { return String(d).slice(0, 10); }
};

export function SourcePicker({ letterId, open, onClose, onPick }) {
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState({});
  const [active, setActive] = useState("photo");
  const [pageFor, setPageFor] = useState(null); // {ref} awaiting page input for a document
  const [page, setPage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dispute-letters/id/${letterId}/citable-sources`);
      setSources(data);
      const first = SECTIONS.find((s) => (data[s.key] || []).length > 0);
      if (first) setActive(first.key);
    } catch { toast.error("Couldn't load your evidence."); } finally { setLoading(false); }
  }, [letterId]);

  useEffect(() => { if (open) { setPageFor(null); setPage(""); load(); } }, [open, load]);

  if (!open) return null;

  const items = sources[active] || [];
  const totalAll = SECTIONS.reduce((n, s) => n + (sources[s.key] || []).length, 0);

  const pick = (it) => {
    const ref = { record_type: it.record_type, record_id: it.record_id };
    if (it.charge_index !== undefined && it.charge_index !== null) ref.charge_index = it.charge_index;
    if (it.record_type === "document") { setPageFor({ ref, label: it.label }); return; }
    onPick(ref);
  };

  const confirmDoc = () => {
    const ref = { ...pageFor.ref };
    if (page && Number(page) > 0) ref.page = Number(page);
    onPick(ref);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" data-testid="source-picker-modal" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E2E8F0]">
          <h4 className="font-heading font-semibold text-navy text-sm flex items-center gap-2"><Search size={15} /> Pick a source to cite</h4>
          <button data-testid="source-picker-close" onClick={onClose} className="text-[#94A3B8]"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#94A3B8]" /></div>
        ) : pageFor ? (
          <div className="p-5 space-y-4" data-testid="source-picker-page">
            <p className="text-sm text-[#334155]">Cite a specific page of <b>{pageFor.label}</b>? (optional)</p>
            <Input data-testid="source-picker-page-input" type="number" min="1" value={page} onChange={(e) => setPage(e.target.value)} placeholder="Page number (optional)" className="rounded-lg" />
            <div className="flex gap-2">
              <Button data-testid="source-picker-page-back" variant="outline" size="sm" className="rounded-lg" onClick={() => { setPageFor(null); setPage(""); }}>Back</Button>
              <Button data-testid="source-picker-page-confirm" size="sm" className="rounded-lg bg-navy text-white" onClick={confirmDoc}>Cite this document</Button>
            </div>
          </div>
        ) : totalAll === 0 ? (
          <div className="p-10 text-center text-sm text-[#64748B]" data-testid="source-picker-empty">
            You don't have any citable records for this property yet. Add photos, documents, payments or other evidence first.
          </div>
        ) : (
          <>
            <div className="flex gap-1 overflow-x-auto px-3 py-2 border-b border-[#F1F5F9]">
              {SECTIONS.filter((s) => (sources[s.key] || []).length > 0).map((s) => {
                const Icon = s.Icon; const n = (sources[s.key] || []).length;
                return (
                  <button key={s.key} data-testid={`source-tab-${s.key}`} onClick={() => setActive(s.key)}
                    className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${active === s.key ? "bg-navy text-white" : "bg-[#F1F5F9] text-[#475569]"}`}>
                    <Icon size={12} /> {s.label} <span className="opacity-70">{n}</span>
                  </button>
                );
              })}
            </div>
            <div className="p-4 overflow-y-auto" data-testid="source-picker-list">
              {active === "photo" ? (
                <div className="grid grid-cols-3 gap-2">
                  {items.map((it) => (
                    <button key={it.record_id} data-testid={`source-item-${it.record_id}`} onClick={() => pick(it)}
                      className="text-left rounded-lg border border-[#E2E8F0] overflow-hidden hover:border-brand transition-colors">
                      <img src={fileUrl(it.thumb_path, 220)} alt="" className="aspect-square object-cover w-full bg-[#F1F5F9]" />
                      <div className="p-1.5">
                        <div className="text-[10px] text-[#475569] truncate">{it.label}</div>
                        <div className="text-[9px] text-[#94A3B8]">{fmtDate(it.date)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {items.map((it) => (
                    <button key={`${it.record_id}-${it.charge_index ?? ""}`} data-testid={`source-item-${it.record_id}${it.charge_index !== undefined && it.charge_index !== null ? "-" + it.charge_index : ""}`}
                      onClick={() => pick(it)} className="w-full text-left rounded-lg border border-[#E2E8F0] p-2.5 hover:border-brand transition-colors">
                      <div className="text-sm text-[#1E293B] truncate">{it.label}</div>
                      <div className="text-[11px] text-[#94A3B8]">{it.source_class}{it.date ? ` · ${fmtDate(it.date)}` : ""}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
